import { useCallback, useEffect, useRef, useState } from 'react';

import { api, type DocContent, type DocPage, type DocSection } from './api';
import { Card, Spinner } from './components/Ui';
import { Shell, type ShellProps } from './Shell';

/* Mermaid is loaded lazily — the library plus its dozens of diagram-type
   modules weigh ~2 MB parsed.  Loading it eagerly caused Out of Memory
   crashes on large documents because the browser allocated all that before
   the page even rendered.  The first MermaidDiagram component triggers the
   one-time import. */
let mermaidPromise: Promise<typeof import('mermaid')> | null = null;
function getMermaid(): Promise<typeof import('mermaid')> {
  if (mermaidPromise === null) {
    mermaidPromise = import('mermaid').then((m) => {
      m.default.initialize({ startOnLoad: false, theme: 'neutral', fontFamily: 'inherit' });
      return m;
    });
  }
  return mermaidPromise;
}

export function Docs(props: ShellProps & { docSlug?: string | undefined }) {
  const { navigate, docSlug } = props;
  const [pages, setPages] = useState<DocPage[] | null>(null);
  const [content, setContent] = useState<DocContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .docPages()
      .then((result) => {
        if (!cancelled) setPages(result.pages);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load docs.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (docSlug === undefined) {
      setContent(null);
      return;
    }
    let cancelled = false;
    api
      .docContent(docSlug)
      .then((result) => {
        if (!cancelled) setContent(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load page.');
      });
    return () => {
      cancelled = true;
    };
  }, [docSlug]);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (pages === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading documentation…" />
        </Card>
      </Shell>
    );
  }

  const activePage = content ?? null;

  return (
    <Shell {...props}>
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-ink">Documentation</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Platform scope, architecture, risks, and open questions
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <nav>
          <Card>
            <ul className="py-2">
              {pages.map((page) => (
                <li key={page.slug}>
                  <button
                    type="button"
                    onClick={() => navigate(`docs/${page.slug}`)}
                    className={`block w-full px-4 py-2 text-left text-sm transition-colors ${
                      docSlug === page.slug
                        ? 'bg-surface-overlay font-medium text-ink'
                        : 'text-ink-muted hover:bg-surface-overlay/50 hover:text-ink'
                    }`}
                  >
                    {page.title}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        <Card className="min-h-[60vh]">
          {activePage !== null ? (
            activePage.sections !== undefined ? (
              <SectionedDoc title={activePage.title} sections={activePage.sections} />
            ) : (
              <div className="px-6 py-5">
                <MarkdownContent content={activePage.content ?? ''} />
              </div>
            )
          ) : (
            <div className="px-6 py-10">
              <h2 className="text-lg font-semibold text-ink">Welcome</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                This documentation covers the full scope of the Staffing Platform — what is built,
                what remains, the risks, and the open questions that need answering before
                production payroll.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                Select a topic from the sidebar to begin.
              </p>
              <ul className="mt-6 space-y-2">
                {pages.map((page) => (
                  <li key={page.slug}>
                    <button
                      type="button"
                      onClick={() => navigate(`docs/${page.slug}`)}
                      className="text-sm text-profit transition-colors hover:underline"
                    >
                      {page.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>
    </Shell>
  );
}

function SectionedDoc({ title, sections }: { title: string; sections: readonly DocSection[] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const section = sections[activeIdx];

  return (
    <div className="flex flex-col">
      <div className="border-b border-edge px-6 py-4">
        <h1 className="text-lg font-bold text-ink">{title}</h1>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {sections.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                i === activeIdx
                  ? 'bg-brand text-white'
                  : 'bg-surface-overlay text-ink-muted hover:bg-surface-overlay/80 hover:text-ink'
              }`}
            >
              {s.heading}
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 py-5">
        {section !== undefined && <MarkdownContent content={section.content} />}
      </div>
    </div>
  );
}

function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) {
        codeLines.push(lines[i] ?? '');
        i += 1;
      }
      i += 1;
      if (lang === 'mermaid') {
        elements.push(<MermaidDiagram key={key++} chart={codeLines.join('\n')} />);
      } else {
        elements.push(
          <pre
            key={key++}
            className="my-3 overflow-x-auto rounded-lg bg-surface-overlay px-4 py-3 text-xs leading-relaxed text-ink-muted"
          >
            <code data-lang={lang}>{codeLines.join('\n')}</code>
          </pre>,
        );
      }
      continue;
    }

    if (line.startsWith('<!-- ')) {
      i += 1;
      continue;
    }

    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={key++} className="mb-4 text-xl font-bold text-ink">
          {inline(line.slice(2))}
        </h1>,
      );
      i += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <h2
          key={key++}
          className="mb-3 mt-8 border-b border-edge pb-2 text-lg font-semibold text-ink"
        >
          {inline(line.slice(3))}
        </h2>,
      );
      i += 1;
      continue;
    }
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="mb-2 mt-6 text-base font-semibold text-ink">
          {inline(line.slice(4))}
        </h3>,
      );
      i += 1;
      continue;
    }

    if (line.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('> ')) {
        quoteLines.push((lines[i] ?? '').slice(2));
        i += 1;
      }
      elements.push(
        <blockquote
          key={key++}
          className="my-3 border-l-3 border-caution-dim pl-4 text-sm text-ink-muted"
        >
          {quoteLines.map((ql, qi) => (
            <p key={qi} className="my-1">
              {inline(ql)}
            </p>
          ))}
        </blockquote>,
      );
      continue;
    }

    if (line.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('|')) {
        tableLines.push(lines[i] ?? '');
        i += 1;
      }
      elements.push(<MarkdownTable key={key++} lines={tableLines} />);
      continue;
    }

    if (/^[-*] /.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^[-*] /.test(lines[i] ?? '')) {
        listItems.push((lines[i] ?? '').replace(/^[-*] /, ''));
        i += 1;
      }
      elements.push(
        <ul key={key++} className="my-3 list-disc space-y-1 pl-6 text-sm text-ink-muted">
          {listItems.map((item, li) => (
            <li key={li}>{inline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i] ?? '')) {
        listItems.push((lines[i] ?? '').replace(/^\d+\. /, ''));
        i += 1;
      }
      elements.push(
        <ol key={key++} className="my-3 list-decimal space-y-1 pl-6 text-sm text-ink-muted">
          {listItems.map((item, li) => (
            <li key={li}>{inline(item)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(
        <div key={key++} className="my-4">
          <img
            src={imgMatch[2]}
            alt={imgMatch[1]}
            className="w-full rounded-lg border border-edge"
          />
        </div>,
      );
      i += 1;
      continue;
    }

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !(lines[i] ?? '').startsWith('#') &&
      !(lines[i] ?? '').startsWith('```') &&
      !(lines[i] ?? '').startsWith('|') &&
      !(lines[i] ?? '').startsWith('> ') &&
      !(lines[i] ?? '').startsWith('<!-- ') &&
      !(lines[i] ?? '').startsWith('![') &&
      !/^[-*] /.test(lines[i] ?? '') &&
      !/^\d+\. /.test(lines[i] ?? '')
    ) {
      paraLines.push(lines[i] ?? '');
      i += 1;
    }
    elements.push(
      <p key={key++} className="my-2 text-sm leading-relaxed text-ink-muted">
        {inline(paraLines.join(' '))}
      </p>,
    );
  }

  return <>{elements}</>;
}

function inline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const full = match[0] ?? '';
    if (full.startsWith('**')) {
      parts.push(
        <strong key={key++} className="font-semibold text-ink">
          {full.slice(2, -2)}
        </strong>,
      );
    } else if (full.startsWith('*')) {
      parts.push(
        <em key={key++} className="italic">
          {full.slice(1, -1)}
        </em>,
      );
    } else if (full.startsWith('`')) {
      parts.push(
        <code
          key={key++}
          className="rounded bg-surface-overlay px-1.5 py-0.5 text-xs font-mono text-ink-muted"
        >
          {full.slice(1, -1)}
        </code>,
      );
    } else if (full.startsWith('[')) {
      const label = match[5] ?? '';
      const href = match[6] ?? '';
      parts.push(
        <span key={key++} className="text-profit">
          {label}
          {href.endsWith('.md') ? '' : ''}
        </span>,
      );
    }
    lastIndex = (match.index ?? 0) + full.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 ? parts[0] : parts;
}

let mermaidCounter = 0;

function MermaidDiagram({ chart }: { chart: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  const render = useCallback(async () => {
    const el = containerRef.current;
    if (el === null) return;
    const id = `mermaid-${String(++mermaidCounter)}`;
    try {
      const m = await getMermaid();
      const { svg } = await m.default.render(id, chart);
      el.innerHTML = svg;
    } catch {
      setFailed(true);
    }
  }, [chart]);

  useEffect(() => {
    void render();
  }, [render]);

  if (failed) {
    return (
      <pre className="my-4 overflow-x-auto rounded-lg border border-edge bg-surface-overlay px-4 py-3 text-xs leading-relaxed text-ink-muted">
        <code>{chart}</code>
      </pre>
    );
  }

  return (
    <div
      ref={containerRef}
      className="my-4 flex justify-center overflow-x-auto rounded-lg border border-edge bg-white px-4 py-6"
    />
  );
}

function MarkdownTable({ lines }: { lines: string[] }) {
  const rows = lines
    .filter((line) => !/^[|\s:-]+$/.test(line.replace(/\|/g, '').trim()) || line.includes('---'))
    .map((line) =>
      line
        .split('|')
        .slice(1, -1)
        .map((cell) => cell.trim()),
    );

  const headerRow = rows[0];
  const isSeparator = (row: string[]) => row.every((cell) => /^:?-+:?$/.test(cell) || cell === '');

  const dataStart = rows.length > 1 && isSeparator(rows[1] ?? []) ? 2 : 1;
  const dataRows = rows.slice(dataStart);

  if (headerRow === undefined) return null;

  return (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-edge">
            {headerRow.map((cell, ci) => (
              <th
                key={ci}
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint"
              >
                {inline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className="border-b border-edge/50">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-ink-muted">
                  {inline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
